import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { firestore } from "../../config/ConfigFirebase";

// Applicants (Extensión) module configuration — the single source of truth for
// which documents an applicant may upload, which are required, how many of each,
// plus the open-ended metadata a future AI classifier / pre-check will read.
// Stored in config/applicants and consumed by BOTH the frontend (this service)
// and the backend (gfunctions/extension/applicants/_shared/applicantConfig.js). Editing it
// is ADMIN-only; reading it is public-safe (the unauthenticated postulación
// forms consume it, exactly like config/global).
//
// Shape is intentionally open: the app only hard-reads the structural fields
// (key, label, stage, required, enabled, min/maxFiles, order, acceptedMime);
// everything else (description, aiHints, keywords, extra{}) is passthrough the
// AI will grow into.

// Seed catalog — mirrors the previously hard-coded lists (constants.js +
// ApplicantRegistrationForm STAGE1_DOCS + ApplicantContinue DOC_LABELS) so behavior
// is identical until an admin edits the config.
const seedDoc = (over) => ({
  key: "",
  label: "",
  stage: 1,
  required: true,
  enabled: true,
  minFiles: 1,
  maxFiles: 1,
  order: 0,
  acceptedMime: ["application/pdf", "image/*"],
  description: "",
  aiHints: "",
  keywords: [],
  extra: {},
  ...over,
});

// Catálogo POR POSTULACIÓN. Los soportes de formación y experiencia ya no viven
// aquí: son documentos de perfil atados a cada registro (ver
// DEFAULT_PROFILE_DOCUMENT_CATALOG). La etapa 1 queda vacía por defecto — un
// admin puede añadir documentos propios de una convocatoria.
export const DEFAULT_DOCUMENT_CATALOG = [
  seedDoc({ key: "antecedente_judicial", label: "Antecedentes judiciales (Policía)", stage: 2, order: 10 }),
  seedDoc({ key: "antecedente_disciplinario", label: "Antecedentes disciplinarios (Procuraduría)", stage: 2, order: 20 }),
  seedDoc({ key: "antecedente_fiscal", label: "Antecedentes fiscales (Contraloría)", stage: 2, order: 30 }),
  seedDoc({ key: "antecedente_correctivos", label: "Medidas correctivas (Policía)", stage: 2, order: 40 }),
  seedDoc({ key: "antecedente_redam", label: "Deudores alimentarios (REDAM)", stage: 2, order: 50 }),
  seedDoc({ key: "antecedente_delitos_sexuales", label: "Inhabilidades por delitos sexuales", stage: 2, order: 60 }),
  seedDoc({ key: "rut", label: "RUT", stage: 2, order: 70 }),
  seedDoc({ key: "certificado_eps", label: "Certificado de EPS", stage: 2, order: 80 }),
];

// Catálogo de DOCUMENTOS DE PERFIL — los que pertenecen a la persona y no a una
// postulación: se cargan una vez y valen para todas las convocatorias.
//
// `boundTo` ata el documento a una lista repetible del perfil: cada archivo
// certifica UN registro (file.entryId). Los dos documentos atados son
// `protected` — se pueden renombrar, volver opcionales o desactivar, pero no
// borrar ni re-apuntar, porque el formulario los pide dentro de su sección.
// Desactivar uno oculta su sección completa en el formulario público.
// `maxFiles` en un documento atado se cuenta POR REGISTRO (0 = ilimitado).
const seedProfileDoc = (over) => seedDoc({ boundTo: null, protected: false, ...over });

export const DEFAULT_PROFILE_DOCUMENT_CATALOG = [
  seedProfileDoc({
    key: "education_certificate",
    label: "Acta de grado / Diploma",
    description: "Soporte de cada formación registrada. Si el título es del exterior, adjunta también la resolución de convalidación.",
    boundTo: "education", protected: true, required: true, maxFiles: 2, order: 10,
  }),
  seedProfileDoc({
    key: "experience_certificate",
    label: "Certificado de experiencia laboral",
    description: "Soporte de cada experiencia registrada, expedido por el empleador.",
    boundTo: "workExperience", protected: true, required: true, maxFiles: 1, order: 20,
  }),
  seedProfileDoc({
    key: "identity_document",
    label: "Documento de identidad",
    description: "Cédula de ciudadanía (ambas caras) o el documento de identificación que registraste.",
    required: true, maxFiles: 2, order: 30,
  }),
];

// Etiquetas de las secciones repetibles del perfil, para el formulario y el panel.
export const PROFILE_SECTION_LABELS = {
  education: "Formación académica",
  workExperience: "Experiencia laboral",
};

// Convocatoria catalog. "General" is the permanent, protected registry every
// applicant is auto-added to; admins may add more calls, each declaring
// which stage-1 documents it requires (a subset of the document catalog). An
// empty requiredDocsStage1 falls back to the global stage-1 required set.
export const DEFAULT_CALLS = [
  { id: "general", name: "General", active: true, protected: true, order: 0, requiredDocsStage1: [] },
];

export const DEFAULT_APPLICANT_CONFIG = {
  documentCatalog: DEFAULT_DOCUMENT_CATALOG,
  profileDocumentCatalog: DEFAULT_PROFILE_DOCUMENT_CATALOG,
  applications: DEFAULT_CALLS,
  idTypes: ["Cédula de ciudadanía", "Cédula de extranjería", "Pasaporte"],
  academicLevels: ["Primaria", "Secundaria", "Técnico", "Tecnólogo", "Profesional", "Especialización", "Maestría", "Doctorado", "Diplomado / Certificación"],
  // Closed catalog of educational institutions, loaded by an ADMIN from a CSV
  // (Extensión → Configuración de aspirantes → Instituciones). While it has
  // entries the formación editor only accepts names from this list; empty means
  // free text. Degrees obtained abroad always allow free text — a national
  // catalog can't list foreign universities.
  institutions: [],
  // Institutions of the OPEN levels: colegios (primaria / secundaria) and the
  // entities behind diplomados / certificaciones, none of them in the catalog
  // above. There the name is free text; every new name joins this list on save
  // (intakeApplicant), for the next applicant to pick and an ADMIN to review.
  openInstitutions: [],
  // Levels an ADMIN marks as open, on top of FIXED_OPEN_LEVELS. Diplomado /
  // certificación is open by default but NOT fixed: an ADMIN may close it.
  openLevels: ["Diplomado / Certificación"],
  maxFileBytes: 10 * 1024 * 1024, // 10 MB
  // SMS verification test mode. When true, Phone Auth runs with
  // appVerificationDisabledForTesting = true: whitelisted "Phone numbers for
  // testing" (Firebase Console → Authentication → Phone) resolve with their
  // canned code and NO real SMS/reCAPTCHA. ⚠️ This DISABLES reCAPTCHA, so REAL
  // numbers fail with auth/invalid-app-credential while it's on — never leave it
  // enabled in production. Default false = normal flow (real reCAPTCHA + SMS).
  smsTestMode: false,
  // Source of truth for where applicant document objects are written. Each
  // environment's config/applicants doc holds its own bucket name; the default
  // seed comes from the env so the field ships populated. Backend resolves the
  // effective bucket straight from here — no bucket env indirection.
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET_APPLICANTS || "",
  // Document AI (OCR) processor. projectId is resolved at runtime server-side
  // (never stored); blank location/processorId fall back to the shipped defaults.
  documentAI: { location: "", processorId: "" },
  // AI reading of the experience certificate (extractExperienceCertificate):
  // OCR text → handleOpenAI. Blank model = gpt-4o-mini. rateLimitPerIp counts
  // certificate reads per IP per 10 minutes (a campus shares one IP).
  experienceAI: { enabled: true, model: "", rateLimitPerIp: 50 },
  // AI reading of the acta de grado / diploma (extractEducationCertificate). Its
  // own switch and model; the per-IP budget is experienceAI.rateLimitPerIp.
  educationAI: { enabled: true, model: "" },
};

// Keys the config screen owns. Kept explicit so a save never clobbers unrelated
// fields, mirroring GLOBAL_CONFIG_KEYS in AppConfigService.
export const APPLICANT_CONFIG_KEYS = Object.keys(DEFAULT_APPLICANT_CONFIG);

const pick = (obj, keys) => Object.fromEntries(keys.filter((k) => k in obj).map((k) => [k, obj[k]]));

// Reads config/applicants, merged over the shipped defaults so consumers always
// get a complete object. Public-safe. Returns defaults on error/miss.
export const getApplicantConfig = async () => {
  try {
    const snap = await getDoc(doc(firestore, "config", "applicants"));
    const data = snap.exists() ? snap.data() : {};
    return { ...DEFAULT_APPLICANT_CONFIG, ...pick(data, APPLICANT_CONFIG_KEYS) };
  } catch (e) {
    console.error("getApplicantConfig error:", e);
    return { ...DEFAULT_APPLICANT_CONFIG };
  }
};

// Merge-writes the applicants config, narrowed to owned keys.
export const saveApplicantConfig = async (patch) => {
  try {
    await setDoc(
      doc(firestore, "config", "applicants"),
      { ...pick(patch, APPLICANT_CONFIG_KEYS), updatedAt: serverTimestamp() },
      { merge: true }
    );
    return true;
  } catch (e) {
    console.error("saveApplicantConfig error:", e);
    return false;
  }
};

// --- Selectors (pure; used by the forms and validation views) ---------------

// Enabled catalog entries for a stage, ordered.
export const docsForStage = (cfg, stage = 1) =>
  (cfg?.documentCatalog || [])
    .filter((d) => d && d.enabled !== false && Number(d.stage || 1) === Number(stage))
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0));

// Required document keys for a stage.
export const requiredDocsForStage = (cfg, stage = 1) =>
  docsForStage(cfg, stage).filter((d) => d.required !== false).map((d) => d.key);

export const docByKey = (cfg, key) =>
  (cfg?.documentCatalog || []).find((d) => d && d.key === key) || null;

export const labelFor = (cfg, key) => docByKey(cfg, key)?.label || key;

// 0 = unlimited (Infinity); anything else floors at 1.
export const maxFilesFor = (cfg, key) => {
  const n = Math.floor(Number(docByKey(cfg, key)?.maxFiles));
  if (n === 0) return Infinity;
  return Number.isFinite(n) && n > 0 ? n : 1;
};

// --- Documentos de perfil ---------------------------------------------------

// Catálogo de perfil completo (habilitados y no), ordenado. Un doc protegido que
// falte en el doc guardado se repone desde la semilla: son parte del formulario,
// no algo que un guardado parcial pueda hacer desaparecer.
export const allProfileDocs = (cfg) => {
  const stored = Array.isArray(cfg?.profileDocumentCatalog) ? cfg.profileDocumentCatalog : [];
  const merged = [...stored];
  for (const seed of DEFAULT_PROFILE_DOCUMENT_CATALOG) {
    if (seed.protected && !merged.some((d) => d && d.key === seed.key)) merged.push(seed);
  }
  return merged.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
};

// Sólo los habilitados: lo que el formulario público pide de verdad.
export const profileDocs = (cfg) => allProfileDocs(cfg).filter((d) => d && d.enabled !== false);

export const profileDocByKey = (cfg, key) =>
  allProfileDocs(cfg).find((d) => d && d.key === key) || null;

export const profileDocLabel = (cfg, key) => profileDocByKey(cfg, key)?.label || key;

// Documento atado a una sección repetible (formación / experiencia), si está
// habilitado. Devuelve null cuando la sección está apagada.
export const profileDocForSection = (cfg, section) =>
  profileDocs(cfg).find((d) => d.boundTo === section) || null;

// Una sección repetible se muestra sólo si su documento atado está habilitado:
// sección y evidencia son la misma decisión de configuración.
export const isProfileSectionEnabled = (cfg, section) => !!profileDocForSection(cfg, section);

export const requiredProfileDocs = (cfg) =>
  profileDocs(cfg).filter((d) => d.required !== false).map((d) => d.key);

// maxFiles de un documento de perfil. En los atados se cuenta POR REGISTRO.
export const maxFilesForProfileDoc = (cfg, key) => {
  const n = Math.floor(Number(profileDocByKey(cfg, key)?.maxFiles));
  if (n === 0) return Infinity;
  return Number.isFinite(n) && n > 0 ? n : 1;
};

// Institution catalog, de-duplicated (case-insensitively) and alphabetically
// ordered — what the formación editor offers. An empty list means "no catalog
// loaded yet", and the editor falls back to free text.
const nameOptions = (list) => {
  const seen = new Set();
  return (Array.isArray(list) ? list : [])
    .map((v) => String(v || "").trim())
    .filter((v) => {
      if (!v) return false;
      const k = v.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .sort((a, b) => a.localeCompare(b, "es"));
};
export const institutionOptions = (cfg) => nameOptions(cfg?.institutions);

// Institutions of the open levels applicants have registered: suggestions for
// the free-text institution of an open-level formación.
export const openInstitutionOptions = (cfg) => nameOptions(cfg?.openInstitutions);

// Levels every environment has, whatever the config says: primaria and
// secundaria — also the fixed OPEN levels (a colegio is free text that grows
// openInstitutions). Mirrors the backend applicantConfig.
export const FIXED_OPEN_LEVELS = ["Primaria", "Secundaria"];

// The configured levels (or the defaults) with the fixed ones always present,
// leading the list.
export const academicLevelsOf = (cfg) => {
  const clean = (Array.isArray(cfg?.academicLevels) ? cfg.academicLevels : [])
    .map((v) => String(v || "").trim()).filter(Boolean);
  const base = clean.length ? clean : DEFAULT_APPLICANT_CONFIG.academicLevels;
  return [...FIXED_OPEN_LEVELS, ...base.filter((l) => !FIXED_OPEN_LEVELS.includes(l))];
};

// The fixed open levels plus the ones an ADMIN marked (default: diplomado).
export const openLevelsOf = (cfg) => [...new Set([
  ...FIXED_OPEN_LEVELS,
  ...(Array.isArray(cfg?.openLevels) ? cfg.openLevels : DEFAULT_APPLICANT_CONFIG.openLevels),
])];

// --- Convocatorias (applications catalog) ----------------------------------

// All catalog applications, General first then by order.
export const allApplications = (cfg) =>
  (cfg?.applications || DEFAULT_CALLS)
    .slice()
    .sort((a, b) => (a.id === "general" ? -1 : b.id === "general" ? 1 : (a.order || 0) - (b.order || 0)));

// Active calls only (General is always active).
export const activeApplications = (cfg) =>
  allApplications(cfg).filter((p) => p && p.active !== false);

export const applicationById = (cfg, id) =>
  allApplications(cfg).find((p) => p && p.id === String(id)) || null;

export const callName = (cfg, id) => applicationById(cfg, id)?.name || id;

// Effective required stage-1 doc keys for a call: its explicit subset,
// or the global stage-1 required set when it declares none (General's case).
export const requiredStage1For = (cfg, id) => {
  const p = applicationById(cfg, id);
  const subset = (p?.requiredDocsStage1 || []).filter(Boolean);
  return subset.length ? subset : requiredDocsForStage(cfg, 1);
};
